import { useLiveQuery } from 'dexie-react-hooks';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useWorkspace } from '@/app/providers/workspace-provider';
import { useOptionalDetailDrawer } from '@/app/providers/detail-drawer-provider';
import { Badge } from '@/components/primitives/badge';
import { Dialog } from '@/components/primitives/dialog';
import { TextInput } from '@/components/primitives/field';
import { searchEverything, type SearchGroupId } from './global-search';

/** 第一版不提供鍵盤式 Command Palette，這裡只做滑鼠可達的搜尋（spec 18）。 */
export function SearchDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { db } = useWorkspace();
  const drawer = useOptionalDetailDrawer();
  const navigate = useNavigate();

  const [keyword, setKeyword] = useState('');
  const [includeTrash, setIncludeTrash] = useState(false);

  const data = useLiveQuery(async () => {
    const [tasks, notes, contacts, followUpLogs, reminders, events] = await Promise.all([
      db.tasks.toArray(),
      db.notes.toArray(),
      db.contacts.toArray(),
      db.followUpLogs.toArray(),
      db.supervisorReminders.toArray(),
      db.calendarEvents.toArray(),
    ]);
    return { tasks, notes, contacts, followUpLogs, reminders, events };
  }, [db]);

  const groups = useMemo(
    () => (data ? searchEverything(keyword, data, { includeTrash }) : []),
    [data, keyword, includeTrash],
  );

  const total = groups.reduce((sum, group) => sum + group.items.length, 0);

  function openResult(groupId: SearchGroupId, taskId: string | null, itemId: string) {
    if (groupId === 'notes') {
      // 筆記本自己會選中清單第一篇；深連結到單一篇要等有 /notes/:id 再說。
      void navigate('/notes');
    } else if (groupId === 'contacts') {
      void navigate('/contacts');
    } else if (groupId === 'events' && !taskId) {
      void navigate('/calendar');
    } else if (taskId) {
      drawer?.openTask(taskId);
    } else {
      void navigate('/tasks');
    }
    void itemId;
    onOpenChange(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="搜尋"
      description="涵蓋任務、便條、筆記、追蹤紀錄、主管提醒、聯絡人與行事曆，包含已完成與已封存的資料。"
      size="lg"
    >
      <div className="space-y-4">
        <div className="relative">
          <MagnifyingGlass
            size={16}
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-text-muted"
          />
          <TextInput
            // oxlint-disable-next-line jsx-a11y/no-autofocus -- 打開搜尋就是為了打字
            autoFocus
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="輸入關鍵字"
            aria-label="搜尋關鍵字"
            className="pl-9"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-[0.8125rem] text-text-muted">
            <input
              type="checkbox"
              checked={includeTrash}
              onChange={(event) => setIncludeTrash(event.target.checked)}
              className="size-4 accent-[var(--accent)]"
            />
            同時搜尋垃圾桶
          </label>
          {keyword.trim() && (
            <span className="tabular text-[0.8125rem] text-text-muted">{total} 筆結果</span>
          )}
        </div>

        {keyword.trim() === '' ? (
          <p className="rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-3 text-[0.875rem] text-text-muted">
            試試看工作標題、對方說過的話、公司名稱或行程名稱。
          </p>
        ) : total === 0 ? (
          <p className="rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-3 text-[0.875rem] text-text-muted">
            找不到符合的資料。換個關鍵字，或勾選「同時搜尋垃圾桶」。
          </p>
        ) : (
          <div className="max-h-[50vh] space-y-5 overflow-y-auto">
            {groups.map((group) => (
              <section key={group.id} className="space-y-1.5">
                <h3 className="flex items-baseline gap-2 border-b border-border pb-1 text-[0.8125rem] font-medium">
                  {group.label}
                  <span className="tabular text-[0.75rem] text-text-muted">
                    {group.items.length}
                  </span>
                </h3>
                <ul className="space-y-1">
                  {group.items.slice(0, 20).map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => openResult(group.id, item.taskId, item.id)}
                        className="w-full rounded-[var(--radius-control-lg)] px-3 py-2 text-left hover:bg-accent-soft"
                      >
                        <p className="flex items-center gap-2 text-[0.875rem]">
                          <span className="min-w-0 truncate">{item.title}</span>
                          {item.inTrash && <Badge tone="neutral">垃圾桶</Badge>}
                        </p>
                        {item.meta && (
                          <p className="tabular text-[0.75rem] text-text-muted">{item.meta}</p>
                        )}
                      </button>
                    </li>
                  ))}
                  {group.items.length > 20 && (
                    <li className="px-3 text-[0.75rem] text-text-muted">
                      還有 {group.items.length - 20} 筆，縮小關鍵字看看。
                    </li>
                  )}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}
